import { useMemo, useState, useEffect } from 'react';

/**
 * Defines default breakpoint values according to Bootstrap 4.
 * Refer to https://getbootstrap.com/docs/4.0/layout/overview/.
 */
var defaultBreakpoints = {
    // Extra small devices (portrait phones, less than 576px)
    xs: 0,
    // Small devices (landscape phones, 576px and up)
    sm: 576,
    // Medium devices (tablets, 768px and up)
    md: 768,
    // Large devices (desktops, 992px and up)
    lg: 992,
    // Extra large devices (large desktops, 1200px and up)
    xl: 1200,
};

/**
 * Default screen layout.
 */
var defaultScreenLayout = {
    layout: undefined,
    isXs: false,
    isSm: false,
    isMd: false,
    isLg: false,
    isXl: false,
    isXsOrBelow: false,
    isSmOrBelow: false,
    isMdOrBelow: false,
    isLgOrBelow: false,
    isXlOrBelow: false,
    isXsOrAbove: false,
    isSmOrAbove: false,
    isMdOrAbove: false,
    isLgOrAbove: false,
    isXlOrAbove: false,
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

/**
 * Defines layout breakpoints for layout names: xs, sm, md, lg, xl.
 */
var LayoutEnum;
(function (LayoutEnum) {
    LayoutEnum[LayoutEnum["xs"] = 1] = "xs";
    LayoutEnum[LayoutEnum["sm"] = 2] = "sm";
    LayoutEnum[LayoutEnum["md"] = 3] = "md";
    LayoutEnum[LayoutEnum["lg"] = 4] = "lg";
    LayoutEnum[LayoutEnum["xl"] = 5] = "xl";
})(LayoutEnum || (LayoutEnum = {}));
var LayoutEnum$1 = LayoutEnum;

/**
 * Returns the current layout.
 * @param param0 options
 */
var getCurrentLayout = function (_a) {
    var breakpoints = _a.breakpoints, width = _a.width;
    if (width <= breakpoints.sm) {
        return __assign(__assign({}, defaultScreenLayout), { 
            // Current layout
            layout: LayoutEnum$1.xs, 
            // Layout conditions
            isXs: true, 
            // Below conditions
            isXsOrBelow: true, isSmOrBelow: true, isMdOrBelow: true, isLgOrBelow: true, isXlOrBelow: true, 
            // Above conditions
            isXsOrAbove: true });
    }
    if (width < breakpoints.md) {
        return __assign(__assign({}, defaultScreenLayout), { 
            // Current layout
            layout: LayoutEnum$1.sm, 
            // Layout conditions
            isSm: true, 
            // Below conditions
            isSmOrBelow: true, isMdOrBelow: true, isLgOrBelow: true, isXlOrBelow: true, 
            // Above conditions
            isXsOrAbove: true, isSmOrAbove: true });
    }
    if (width < breakpoints.lg) {
        return __assign(__assign({}, defaultScreenLayout), { 
            // Current layout
            layout: LayoutEnum$1.md, 
            // Layout conditions
            isMd: true, 
            // Below conditions
            isMdOrBelow: true, isLgOrBelow: true, isXlOrBelow: true, 
            // Above conditions
            isXsOrAbove: true, isSmOrAbove: true, isMdOrAbove: true });
    }
    if (width < breakpoints.xl) {
        return __assign(__assign({}, defaultScreenLayout), { 
            // Current layout
            layout: LayoutEnum$1.lg, 
            // Layout conditions
            isLg: true, 
            // Below conditions
            isLgOrBelow: true, isXlOrBelow: true, 
            // Above conditions
            isXsOrAbove: true, isSmOrAbove: true, isMdOrAbove: true, isLgOrAbove: true });
    }
    return __assign(__assign({}, defaultScreenLayout), { 
        // Current layout
        layout: LayoutEnum$1.xl, 
        // Layout conditions
        isXl: true, 
        // Below conditions
        isXlOrBelow: true, 
        // Above conditions
        isXsOrAbove: true, isSmOrAbove: true, isMdOrAbove: true, isLgOrAbove: true, isXlOrAbove: true });
};

/**
 * Returns the current window size state.
 * @param isClientSide Defines if the window object is present.
 * @param breakpoints Defines custom breakpoints.
 */
var getWindowSize = function (_a) {
    var _b, _c;
    var isClientSide = _a.isClientSide, breakpoints = _a.breakpoints;
    if (!isClientSide) {
        return {
            width: undefined,
            height: undefined,
            screenLayout: defaultScreenLayout,
        };
    }
    var width = ('innerWidth' in window
        ? window.innerWidth
        : (_b = document === null || document === void 0 ? void 0 : document.documentElement) === null || _b === void 0 ? void 0 : _b.offsetWidth);
    var height = ('innerHeight' in window
        ? window.innerHeight
        : (_c = document === null || document === void 0 ? void 0 : document.documentElement) === null || _c === void 0 ? void 0 : _c.offsetHeight);
    var screenLayout = getCurrentLayout({
        breakpoints: breakpoints,
        width: width,
    });
    return {
        screenLayout: screenLayout,
        height: height,
        width: width,
    };
};

/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Hook that executes a callback when the component mounts.
 */
var useDidMount = function (callback) {
    useEffect(callback, []);
};

/**
 * Default hook options.
 */
var defaultOptions = {
    useDebounce: true,
    debounceTimeMs: 200,
    breakpoints: defaultBreakpoints,
};
/**
 * Hook that monitors window size, and updates the object at the end of each window resize.
 * It also returns the actual screen layout - one of xl, lg, md, sm, xs.
 */
var useWindowSize = function (options) {
    if (options === void 0) { options = defaultOptions; }
    var isClientSide = useMemo(function () { return (typeof window === 'object'
        && typeof document !== 'undefined'); }, []);
    var breakpoints = useMemo(function () {
        var _a;
        return ((_a = options.breakpoints) !== null && _a !== void 0 ? _a : defaultOptions.breakpoints);
    }, [options]);
    var useDebounce = useMemo(function () {
        var _a;
        return ((_a = options.useDebounce) !== null && _a !== void 0 ? _a : defaultOptions.useDebounce);
    }, [options]);
    var debounceTimeMs = useMemo(function () {
        var _a;
        return ((_a = options.debounceTimeMs) !== null && _a !== void 0 ? _a : defaultOptions.debounceTimeMs);
    }, [options]);
    var _a = useState(getWindowSize({
        isClientSide: isClientSide,
        breakpoints: breakpoints,
    })), windowSize = _a[0], setWindowSize = _a[1];
    useDidMount(function () {
        if (!isClientSide) {
            return function () { };
        }
        var resizeHandler;
        var handleResize = function () {
            if (!useDebounce) {
                setWindowSize(getWindowSize({
                    isClientSide: isClientSide,
                    breakpoints: breakpoints,
                }));
                return;
            }
            clearTimeout(resizeHandler);
            resizeHandler = setTimeout(function () {
                setWindowSize(getWindowSize({
                    isClientSide: isClientSide,
                    breakpoints: breakpoints,
                }));
            }, debounceTimeMs);
        };
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    });
    return windowSize;
};

export { LayoutEnum$1 as layout, useWindowSize };
//# sourceMappingURL=index.es.js.map
